import {
  Component,
  Input,
  EventEmitter,
  Output,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CollapsibleSectionComponent } from '../../../shared/collapsible-section/collapsible-section.component';
import { FacebookAdPreviewComponent } from '../../facebook-ad-preview/facebook-ad-preview.component';
import { ApiService } from '../../../services/api.service';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { findFacebookContent } from '../optional-setting/sms-delivery.mapper';

@Component({
  selector: 'app-facebook',
  imports: [CommonModule, CollapsibleSectionComponent, FacebookAdPreviewComponent, FormsModule],
  templateUrl: './facebook.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './facebook.component.scss',
})
export class FacebookComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isExpanded = true;
  @Input() campaignData: any = {};
  @Input() isCompleted = false;
  @Output() toggle = new EventEmitter<boolean>();
  @Output() done = new EventEmitter<void>();
  @Output() dataChange = new EventEmitter<any>();
  @Output() dataClear = new EventEmitter<void>();

  showCreateModal = false;
  isUploadingLogo = false;
  isUploadingImage = false;
  isEditMode = false;
  facebookAdData = {
    name: '',
    caption: '',
    headline: '',
    buttonText: '',
    logo: null as File | null,
    image: null as File | null,
    logoUrl: '',
    imageUrl: '',
  };

  // Validation properties
  validationErrors = {
    name: '',
    caption: '',
    headline: '',
    buttonText: '',
    logo: '',
    image: '',
  };

  // Add these properties to cache object URLs
  private logoObjectUrl = '';
  private imageObjectUrl = '';

  constructor(
    private apiService: ApiService,
    private messageService: NotificationService
  ) {}

  ngOnInit(): void {
    this.applyFacebookContentFromCampaign();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['campaignData']) {
      this.applyFacebookContentFromCampaign();
    }
  }

  private applyFacebookContentFromCampaign(): void {
    const facebookContent = findFacebookContent(this.campaignData?.contents);
    if (facebookContent) {
      this.facebookAdData = {
        name: facebookContent.fb_name || '',
        caption: facebookContent.fb_caption || '',
        headline: facebookContent.fb_headline || '',
        buttonText: facebookContent.fb_button_text || '',
        logo: null,
        image: null,
        logoUrl: facebookContent.fb_logo_img || '',
        imageUrl: facebookContent.fb_img || '',
      };
    }
  }

  onFacebookToggle(expanded: boolean): void {
    this.toggle.emit(expanded);
  }

  onFacebookDone(): void {
    // Check if button text is "Create" or "Done" based on hasFacebookContent
    if (!this.hasFacebookContent()) {
      // Button shows "Create" - open the create Facebook ad modal
      this.openCreateModal();
    } else {
      // Button shows "Done" - emit the done event (keep old behavior)
      this.done.emit();
    }
  }

  onEditMessage(): void {
    console.log('Edit message clicked');
  }

  /**
   * Reset the facebookAdData object to its default empty values.
   * @example
   * this.resetFacebookAdData()
   * // undefined
   * @returns {void} No return value.
   **/
  private resetFacebookAdData(): void {
    this.facebookAdData = {
      name: '',
      caption: '',
      headline: '',
      buttonText: '',
      logo: null,
      image: null,
      logoUrl: '',
      imageUrl: '',
    };
  }

  onDeleteMessage(): void {
    console.log('Delete message clicked');
    this.resetFacebookAdData();
    this.dataClear.emit();
  }

  /**
   * Prepare and open the Facebook ad creation modal, resetting form state when not editing existing content.
   * @example
   * penCreateModal()
   * void
   * @returns {{void}} No return value; updates component state to show the create modal.
   **/
  openCreateModal(): void {
    // Determine if we're in edit mode by checking if we have existing data
    this.isEditMode = this.hasFacebookContent();

    // Only reset form data if we're creating (not editing)
    if (!this.isEditMode) {
      this.resetFacebookAdData();

      // Clean up object URLs
      if (this.logoObjectUrl) {
        URL.revokeObjectURL(this.logoObjectUrl);
        this.logoObjectUrl = '';
      }
      if (this.imageObjectUrl) {
        URL.revokeObjectURL(this.imageObjectUrl);
        this.imageObjectUrl = '';
      }

      // Reset file inputs
      setTimeout(() => {
        const logoInput = document.getElementById('logo-upload') as HTMLInputElement;
        const imageInput = document.getElementById('image-upload') as HTMLInputElement;
        if (logoInput) {
          logoInput.value = '';
        }
        if (imageInput) {
          imageInput.value = '';
        }
      }, 0);
    }

    // Clear validation errors
    this.clearValidationErrors();

    this.showCreateModal = true;
  }

  closeCreateModal(): void {
    this.showCreateModal = false;
    // Reset edit mode flag when closing modal
    this.isEditMode = false;
  }

  /**
   * Validate and save the current Facebook ad from the editor, close the create modal, show a success toast when creating a new ad, and emit a form change event.
   * @example
   * aveFacebookAd()
   * undefined
   * @param {void} none - No parameters.
   * @returns {void} Nothing is returned.
   */
  saveFacebookAd(): void {
    // Clear previous validation errors
    this.clearValidationErrors();

    // Validate required fields
    const isValid = this.validateForm();

    if (!isValid) {
      console.log('Form validation failed');
      return; // Don't close modal or save if validation fails
    }

    console.log('Saving Facebook Ad:', this.facebookAdData);

    // Show success toast message only for create, not for edit (check before closing modal)
    const isCreating = !this.isEditMode;

    this.closeCreateModal();

    if (isCreating) {
      this.messageService.add({
        severity: 'success',
        summary: 'Facebook Ad created!',
        detail: 'A new Facebook ad campaign has been created successfully.',
      });
    }

    // Emit data change event
    this.onFormChange();
    this.releaseLocalLogoPreview();
    this.releaseLocalImagePreview();
  }

  /**
   * Emit the current Facebook ad fields assembled from the component's facebookAdData to notify listeners of a data change.
   * @example
   * nFormChange()
   * // Emits an object, e.g.:
   * // {
   * //   name: "Ad Name",
   * //   caption: "Caption text",
   * //   headline: "Headline text",
   * //   buttonText: "Click Here",
   * //   logo: "logoFile",
   * //   logoUrl: "https://example.com/logo.png",
   * //   image: "imageFile",
   * //   imageUrl: "https://example.com/image.png"
   * // }
   * @returns {void} Void; the function emits an event via this.dataChange and does not return a value.
   **/
  onFormChange(): void {
    const facebookData = {
      name: this.facebookAdData.name,
      caption: this.facebookAdData.caption,
      headline: this.facebookAdData.headline,
      buttonText: this.facebookAdData.buttonText,
      logo: this.facebookAdData.logo,
      logoUrl: this.facebookAdData.logoUrl,
      image: this.facebookAdData.image,
      imageUrl: this.facebookAdData.imageUrl,
    };

    this.dataChange.emit(facebookData);
  }

  /**
   * Determine whether the current campaign has any Facebook-specific content defined.
   * @example
   * this.asFacebookContent()
   * true
   * @returns {boolean} True if any Facebook content fields (fb_name, fb_caption, fb_headline, fb_button_text, fb_logo_img, or fb_img) exist on campaignData.contents.facebook, otherwise false.
   **/
  hasFacebookContent(): boolean {
    const facebookContent = findFacebookContent(this.campaignData?.contents);
    if (facebookContent) {
      return Boolean(
        facebookContent.fb_name?.trim() ||
        facebookContent.fb_caption?.trim() ||
        facebookContent.fb_headline?.trim() ||
        facebookContent.fb_button_text?.trim() ||
        facebookContent.fb_logo_img?.trim() ||
        facebookContent.fb_img?.trim()
      );
    }

    return Boolean(
      this.facebookAdData.name?.trim() ||
      this.facebookAdData.caption?.trim() ||
      this.facebookAdData.headline?.trim() ||
      this.facebookAdData.buttonText?.trim() ||
      this.facebookAdData.logoUrl?.trim() ||
      this.facebookAdData.imageUrl?.trim()
    );
  }

  // File upload methods
  onLogoUpload(event: any): void {
    const [file] = event.target.files || [];
    if (file) {
      this.validateAndUploadLogo(file);
    }
  }

  onImageUpload(event: any): void {
    const [file] = event.target.files || [];
    if (file) {
      this.validateAndUploadImage(file);
    }
  }

  // Drag and drop methods
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onLogoDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.validateAndUploadLogo(files[0]);
    }
  }

  onImageDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.validateAndUploadImage(files[0]);
    }
  }

  /**
   * Validate the provided logo File (type, size, dimensions), clear previous logo errors, and proceed with dimension validation.
   * @example
   * validateAndUploadLogo(file)
   * void
   * @param {{File}} {{file}} - The logo file to validate and upload.
   * @returns {{void}} No return value; may trigger further upload steps after validation.
   **/
  private validateAndUploadLogo(file: File): void {
    // Clear previous logo error
    this.validationErrors.logo = '';

    // First validate file type and size
    if (!this.validateFile(file, 'logo')) {
      return;
    }

    // Then validate logo dimensions
    this.validateLogoDimensions(file);
  }

  /**
   * Validates that a logo image file has a 1:1 aspect ratio and updates component state or uploads the logo.
   * @example
   * validateLogoDimensions(file)
   * undefined
   * @param {{File}} {{file}} - The image File to validate (expected a square/logo image).
   * @returns {{void}} Performs validation and updates component properties (validationErrors.logo, facebookAdData.logo, logoObjectUrl) or calls uploadLogo; does not return a value.
   **/
  private validateLogoDimensions(file: File): void {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = e => {
      const imageUrl = e.target?.result as string;
      img.onload = () => {
        const width = img.width;
        const height = img.height;
        const aspectRatio = width / height;
        const requiredAspectRatio = 1.0; // 1:1 aspect ratio
        const tolerance = 0.01; // Allow small tolerance for aspect ratio

        // Check aspect ratio (mandatory - must be 1:1)
        if (Math.abs(aspectRatio - requiredAspectRatio) > tolerance) {
          this.validationErrors.logo = `Logo aspect ratio must be 1:1 (mandatory). Current aspect ratio: ${aspectRatio.toFixed(2)}:1 (${width} x ${height} pixels). Recommended: 1080 x 1080 pixels.`;
          this.facebookAdData.logo = null;
          this.logoObjectUrl = '';
          return;
        }

        // Dimensions are valid, proceed with logo selection
        this.facebookAdData.logoUrl = '';
        this.facebookAdData.logo = file;
        this.setLogoObjectUrl(file);
        this.uploadLogo(file);
      };

      img.onerror = () => {
        this.validationErrors.logo = 'Failed to load logo. Please try again.';
        this.facebookAdData.logo = null;
        this.logoObjectUrl = '';
      };

      img.src = imageUrl;
    };

    reader.onerror = () => {
      this.validationErrors.logo = 'Failed to read logo file. Please try again.';
      this.facebookAdData.logo = null;
      this.logoObjectUrl = '';
    };

    reader.readAsDataURL(file);
  }

  /**
   * Validate an image file (type, size, and dimensions) and upload it if valid.
   * @example
   * validateAndUploadImage(sampleFile)
   * undefined
   * @param {{File}} {{file}} - The image File object to validate (type/size) and check dimensions for.
   * @returns {{void}} Does not return a value; exits early if validation fails.
   **/
  private validateAndUploadImage(file: File): void {
    // Clear previous image error
    this.validationErrors.image = '';

    // First validate file type and size
    if (!this.validateFile(file, 'image')) {
      return;
    }

    // Then validate image dimensions
    this.validateImageDimensions(file);
  }

  /**
   * Validates a selected image file's dimensions and aspect ratio for Facebook ad requirements and updates component state accordingly.
   * @example
   * validateImageDimensions(selectedFile)
   * undefined
   * @param {{File}} {{file}} - The image File object selected by the user.
   * @returns {{void}} Nothing. Updates validationErrors, facebookAdData, and imageObjectUrl on the component and calls uploadImage(file) when valid.
   **/
  private validateImageDimensions(file: File): void {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = e => {
      const imageUrl = e.target?.result as string;
      img.onload = () => {
        const { width, height } = img;
        const aspectRatio = width / height;
        const requiredAspectRatio = 1.91;
        const minWidth = 600;
        const minHeight = 314;
        const tolerance = 0.01; // Allow small tolerance for aspect ratio

        // Check aspect ratio (mandatory)
        if (Math.abs(aspectRatio - requiredAspectRatio) > tolerance) {
          this.validationErrors.image = `Image aspect ratio must be 1.91:1 (mandatory). Current aspect ratio: ${aspectRatio.toFixed(2)}:1 (${width} x ${height} pixels)`;
          this.facebookAdData.image = null;
          this.imageObjectUrl = '';
          return;
        }

        // Check minimum dimensions
        if (width < minWidth || height < minHeight) {
          this.validationErrors.image = `Image dimensions must be at least ${minWidth} x ${minHeight} pixels. Current dimensions: ${width} x ${height} pixels. Recommended: 1200 x 628 pixels.`;
          this.facebookAdData.image = null;
          this.imageObjectUrl = '';
          return;
        }

        // Dimensions are valid, proceed with image selection
        this.facebookAdData.imageUrl = '';
        this.facebookAdData.image = file;
        this.setImageObjectUrl(file);
        this.uploadImage(file);
      };

      img.onerror = () => {
        this.validationErrors.image = 'Failed to load image. Please try again.';
        this.facebookAdData.image = null;
        this.imageObjectUrl = '';
      };

      img.src = imageUrl;
    };

    reader.onerror = () => {
      this.validationErrors.image = 'Failed to read image file. Please try again.';
      this.facebookAdData.image = null;
      this.imageObjectUrl = '';
    };

    reader.readAsDataURL(file);
  }

  /**
   * Validate an image file for allowed types (PNG/JPEG) and maximum size (5MB).
   * @example
   * validateFile(file, 'logo')
   * true
   * @param {File} file - The File object to validate.
   * @param {'logo'|'image'} fieldType - The field type ('logo' or 'image') used to record validation errors.
   * @returns {boolean} True if the file is a permitted image type and within the 5MB size limit; otherwise false.
   */
  private validateFile(file: File, fieldType: 'logo' | 'image'): boolean {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      this.validationErrors[fieldType] =
        'Please upload a valid image file (.png, .jpg, .jpeg). PDF files are not allowed.';
      return false;
    }

    if (file.size > maxSize) {
      this.validationErrors[fieldType] = 'File size must be less than 5MB';
      return false;
    }

    return true;
  }

  /**
   * Uploads a logo file to the server and updates component state based on the response.
   * @example
   * uploadLogo(new File(['content'], 'logo.png', { type: 'image/png' }))
   * undefined
   * @param {{File}} {{file}} - The image file to upload for the campaign logo.
   * @returns {{void}} Nothing is returned; the method updates component state and handles success/error internally.
   **/
  private uploadLogo(file: File): void {
    this.isUploadingLogo = true;
    const formData = new FormData();
    formData.append('fb_logo_img', file);

    this.apiService.uploadCampaignFile(formData).subscribe({
      next: response => {
        this.isUploadingLogo = false;
        if (response.status && response.data && response.data.fb_logo_img) {
          this.facebookAdData.logoUrl = response.data.fb_logo_img;
          this.validationErrors.logo = '';
          console.log('Logo uploaded successfully:', this.facebookAdData.logoUrl);
        } else {
          console.error('Logo upload failed: Invalid response structure');
          this.validationErrors.logo = 'Logo upload failed. Please try again.';
          this.facebookAdData.logo = null;
          this.releaseLogoObjectUrl();
        }
      },
      error: error => {
        this.isUploadingLogo = false;
        console.error('Error uploading logo:', error);
        this.validationErrors.logo = 'Failed to upload logo. Please try again.';
        this.facebookAdData.logo = null;
        this.releaseLogoObjectUrl();
      },
    });
  }

  /**
   * Uploads an image file to the campaign upload endpoint, updates component state with the returned image URL, and handles success and error validation states.
   * @example
   * uploadImage(file)
   * void
   * @param {{File}} {{file}} - The image File object to upload to the server.
   * @returns {{void}} Does not return a value; updates component state (facebookAdData.imageUrl, imageObjectUrl, validationErrors, isUploadingImage) based on upload result.
   **/
  private uploadImage(file: File): void {
    this.isUploadingImage = true;
    const formData = new FormData();
    formData.append('fb_img', file);

    this.apiService.uploadCampaignFile(formData).subscribe({
      next: response => {
        this.isUploadingImage = false;
        if (response.status && response.data && response.data.fb_img) {
          this.facebookAdData.imageUrl = response.data.fb_img;
          this.validationErrors.image = '';
          console.log('Image uploaded successfully:', this.facebookAdData.imageUrl);
        } else {
          console.error('Image upload failed: Invalid response structure');
          this.validationErrors.image = 'Image upload failed. Please try again.';
          this.facebookAdData.image = null;
          this.releaseImageObjectUrl();
        }
      },
      error: error => {
        this.isUploadingImage = false;
        console.error('Error uploading image:', error);
        this.validationErrors.image = 'Failed to upload image. Please try again.';
        this.facebookAdData.image = null;
        this.releaseImageObjectUrl();
      },
    });
  }

  removeLogo(): void {
    this.facebookAdData.logo = null;
    this.facebookAdData.logoUrl = '';
    this.validationErrors.logo = '';
    this.releaseLogoObjectUrl();
  }

  removeImage(): void {
    this.facebookAdData.image = null;
    this.facebookAdData.imageUrl = '';
    this.validationErrors.image = '';
    this.releaseImageObjectUrl();
  }

  /**
   * Returns the URL to use for the Facebook ad logo. Prefers server-provided logoUrl, then a cached object URL, or creates and caches an object URL from a File if available; otherwise returns an empty string.
   * @example
   * etLogoUrl()
   * "https://cdn.example.com/logo.png"
   * @returns {string} The resolved logo URL, or an empty string when no logo is available.
   */
  getLogoUrl(): string {
    if (this.logoObjectUrl) {
      return this.logoObjectUrl;
    }

    if (this.facebookAdData.logoUrl) {
      return this.facebookAdData.logoUrl;
    }

    if (this.facebookAdData.logo instanceof File) {
      this.setLogoObjectUrl(this.facebookAdData.logo);
      return this.logoObjectUrl;
    }

    return '';
  }

  private setLogoObjectUrl(file: File): void {
    this.releaseLogoObjectUrl();
    this.logoObjectUrl = URL.createObjectURL(file);
  }

  private releaseLogoObjectUrl(): void {
    if (this.logoObjectUrl) {
      URL.revokeObjectURL(this.logoObjectUrl);
      this.logoObjectUrl = '';
    }
  }

  /** After save, preview uses the API logo URL instead of the local blob. */
  private releaseLocalLogoPreview(): void {
    this.facebookAdData.logo = null;
    this.releaseLogoObjectUrl();
  }

  /**
   * Returns the image URL to use for the Facebook ad, preferring the server-provided URL, then a cached object URL, and creating a blob URL from a File if necessary.
   * @example
   * etImageUrl()
   * "https://server.example.com/path/to/image.jpg" or "blob:null/..." or ""
   * @returns {string} The resolved image URL, or an empty string if no image is available.
   **/
  getImageUrl(): string {
    if (this.imageObjectUrl) {
      return this.imageObjectUrl;
    }

    if (this.facebookAdData.imageUrl) {
      return this.facebookAdData.imageUrl;
    }

    if (this.facebookAdData.image instanceof File) {
      this.setImageObjectUrl(this.facebookAdData.image);
      return this.imageObjectUrl;
    }

    return '';
  }

  private setImageObjectUrl(file: File): void {
    this.releaseImageObjectUrl();
    this.imageObjectUrl = URL.createObjectURL(file);
  }

  private releaseImageObjectUrl(): void {
    if (this.imageObjectUrl) {
      URL.revokeObjectURL(this.imageObjectUrl);
      this.imageObjectUrl = '';
    }
  }

  /** After save, preview uses the API image URL instead of the local blob. */
  private releaseLocalImagePreview(): void {
    this.facebookAdData.image = null;
    this.releaseImageObjectUrl();
  }

  // Block special characters in name field
  onNameKeyPress(event: KeyboardEvent): boolean {
    const char = String.fromCharCode(event.which || event.keyCode);
    // Allow only alphanumeric characters and spaces
    const pattern = /[a-zA-Z0-9\s]/;
    if (!pattern.test(char)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  // Filter special characters from pasted content in name field
  onNameInput(event: any): void {
    const { value } = event.target;
    // Remove any special characters, keep only alphanumeric and spaces
    const filteredValue = value.replace(/[^a-zA-Z0-9\s]/g, '');
    if (value !== filteredValue) {
      this.facebookAdData.name = filteredValue;
    }
  }

  // Validation methods
  /**
   * Validate the Facebook ad form fields (name, caption, headline, buttonText, logo/image) and populate validationErrors.
   * @example
   * this.validateForm()
   * true
   * @returns {{boolean}} True if all required fields are valid, otherwise false.
   **/
  private validateForm(): boolean {
    let isValid = true;

    // Validate name
    if (!this.facebookAdData.name || this.facebookAdData.name.trim() === '') {
      this.validationErrors.name = 'Name is required';
      isValid = false;
    } else if (this.facebookAdData.name.length > 100) {
      this.validationErrors.name = 'Name cannot exceed 100 characters';
      isValid = false;
    }

    // Validate caption
    if (!this.facebookAdData.caption || this.facebookAdData.caption.trim() === '') {
      this.validationErrors.caption = 'Caption is required';
      isValid = false;
    } else if (this.facebookAdData.caption.length > 500) {
      this.validationErrors.caption = 'Caption cannot exceed 500 characters';
      isValid = false;
    }

    // Validate headline
    if (!this.facebookAdData.headline || this.facebookAdData.headline.trim() === '') {
      this.validationErrors.headline = 'Headline is required';
      isValid = false;
    } else if (this.facebookAdData.headline.length > 100) {
      this.validationErrors.headline = 'Headline cannot exceed 100 characters';
      isValid = false;
    }

    // Validate button text
    if (!this.facebookAdData.buttonText || this.facebookAdData.buttonText.trim() === '') {
      this.validationErrors.buttonText = 'Button text is required';
      isValid = false;
    } else if (this.facebookAdData.buttonText.length > 50) {
      this.validationErrors.buttonText = 'Button text cannot exceed 50 characters';
      isValid = false;
    }

    // Validate logo (local preview, uploaded file, or saved API URL)
    if (!this.facebookAdData.logo && !this.facebookAdData.logoUrl && !this.logoObjectUrl) {
      this.validationErrors.logo =
        'Logo is required (Aspect Ratio: 1:1, Recommended: 1080 x 1080 pixels)';
      isValid = false;
    }

    // Validate image (local preview, uploaded file, or saved API URL)
    if (!this.facebookAdData.image && !this.facebookAdData.imageUrl && !this.imageObjectUrl) {
      this.validationErrors.image =
        'Image is required (Aspect Ratio: 1.91:1, Minimum: 600 x 314 pixels)';
      isValid = false;
    }

    return isValid;
  }

  private clearValidationErrors(): void {
    this.validationErrors = {
      name: '',
      caption: '',
      headline: '',
      buttonText: '',
      logo: '',
      image: '',
    };
  }

  // Add cleanup method to prevent memory leaks
  ngOnDestroy(): void {
    this.releaseLogoObjectUrl();
    this.releaseImageObjectUrl();
  }
}
